/**
 * OptionComparisonView — Phase 3B-2 (Sec 21)
 * ─────────────────────────────────────────────────────────────────────────────
 * Renders 3 candidate laydowns side-by-side (Quality / Cost / Balanced).
 * Each card shows verdict badge, key KPIs (cost/kg, Mic CV%, risk score,
 * R-grade %, top-supplier %), warnings, and a "Pick this" button.
 *
 * Used by:
 *   - QuickLaydownPage (operator tablet)
 *   - LaydownPlanningPage Suggest drawer (QC supervisor)
 *
 * Picking an option fires onPick(mode) — the parent then calls the regular
 * /suggest endpoint with mode=<chosen> + persist=true to save it.
 */
import { Alert, Button, Card, Col, Row, Space, Statistic, Tag, Typography } from 'antd'
import { CheckCircleFilled, WarningFilled, CloseCircleFilled } from '@ant-design/icons'
import VerdictBadge from './VerdictBadge'

const { Text } = Typography

type Mode = 'quality' | 'cost' | 'balanced'

type Option = {
  mode: Mode
  selected_count: number
  target_bales: number
  within_band: string | null
  drift_status: string | null
  cost_per_kg_avg: number | null
  cost_per_kg_min: number | null
  cost_per_kg_max: number | null
  risk_score: number | null
  risk_level: string | null
  r_grade_pct: number | null
  warnings: string[]
  within_variance_json: Record<string, { mean?: number; cv_pct?: number }> | null
  supplier_breakdown_json?: Array<{ supplier_name: string; pct: number }> | null
}

type Options = {
  quality: Option
  cost: Option
  balanced: Option
}

const MODE_LABEL: Record<Mode, string> = {
  quality: 'Best Quality',
  cost: 'Lowest Safe Cost',
  balanced: 'Balanced',
}

const MODE_COLOR: Record<Mode, string> = {
  quality: '#52c41a',
  cost: '#1677ff',
  balanced: '#722ed1',
}

const MODE_HINT: Record<Mode, string> = {
  quality: 'Tightest CV%, may use premium cotton',
  cost: 'Cheapest input within tier limits',
  balanced: '60% quality + 40% cost composite',
}

const RISK_COLOR: Record<string, string> = {
  LOW: '#52c41a',
  MEDIUM: '#faad14',
  HIGH: '#ff4d4f',
}

function OptionCard({ opt, onPick, picking }: {
  opt: Option
  onPick: () => void
  picking: boolean
}) {
  const micCv = opt.within_variance_json?.mic?.cv_pct
  const topSupplier = opt.supplier_breakdown_json?.[0]
  return (
    <Card
      size="small"
      style={{ height: '100%', borderColor: MODE_COLOR[opt.mode], borderWidth: 2 }}
      title={
        <Space>
          <span style={{ color: MODE_COLOR[opt.mode], fontSize: 14, fontWeight: 700 }}>
            {MODE_LABEL[opt.mode]}
          </span>
        </Space>
      }
      extra={
        <Tag color={opt.risk_level === 'LOW' ? 'green'
          : opt.risk_level === 'MEDIUM' ? 'orange' : 'red'}>
          Risk {opt.risk_score?.toFixed(0)} ({opt.risk_level})
        </Tag>
      }
      styles={{ body: { padding: 12 } }}
    >
      <Text type="secondary" style={{ fontSize: 11 }}>{MODE_HINT[opt.mode]}</Text>

      <div style={{ marginTop: 8 }}>
        <VerdictBadge band={opt.within_band ?? null} size="compact"
          baleCount={opt.selected_count} />
      </div>

      <Row gutter={8} style={{ marginTop: 12 }}>
        <Col span={12}>
          <Statistic
            title="Cost / kg"
            value={opt.cost_per_kg_avg != null ? `₹${opt.cost_per_kg_avg.toFixed(2)}` : '—'}
            valueStyle={{ fontSize: 16, color: opt.mode === 'cost' ? MODE_COLOR.cost : undefined }}
          />
          {opt.cost_per_kg_min != null && opt.cost_per_kg_max != null && (
            <Text type="secondary" style={{ fontSize: 10 }}>
              ₹{opt.cost_per_kg_min.toFixed(0)}–{opt.cost_per_kg_max.toFixed(0)}
            </Text>
          )}
        </Col>
        <Col span={12}>
          <Statistic
            title="Mic CV%"
            value={micCv != null ? micCv.toFixed(2) : '—'}
            suffix={micCv != null ? '%' : ''}
            valueStyle={{ fontSize: 16, color: opt.mode === 'quality' ? MODE_COLOR.quality : undefined }}
          />
        </Col>
        <Col span={12} style={{ marginTop: 8 }}>
          <Statistic
            title="R-grade"
            value={opt.r_grade_pct != null ? `${opt.r_grade_pct.toFixed(1)}%` : '—'}
            valueStyle={{ fontSize: 14 }}
          />
        </Col>
        <Col span={12} style={{ marginTop: 8 }}>
          <Statistic
            title="Top supplier"
            value={topSupplier ? `${topSupplier.pct.toFixed(0)}%` : '—'}
            valueStyle={{ fontSize: 14 }}
          />
        </Col>
      </Row>

      {opt.warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8, fontSize: 11 }}
          message={`${opt.warnings.length} warning${opt.warnings.length === 1 ? '' : 's'}`}
          description={
            <ul style={{ margin: 0, paddingLeft: 14, fontSize: 11 }}>
              {opt.warnings.slice(0, 3).map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          }
        />
      )}

      <Button
        type="primary" block
        style={{ marginTop: 12, background: MODE_COLOR[opt.mode], borderColor: MODE_COLOR[opt.mode] }}
        onClick={onPick}
        loading={picking}
        disabled={opt.selected_count === 0}
      >
        Use this option
      </Button>
    </Card>
  )
}

export default function OptionComparisonView({
  options, onPick, picking,
}: {
  options: Options
  onPick: (mode: Mode) => void
  picking: Mode | null
}) {
  return (
    <Row gutter={12}>
      <Col xs={24} md={8}>
        <OptionCard opt={options.quality}
          onPick={() => onPick('quality')}
          picking={picking === 'quality'} />
      </Col>
      <Col xs={24} md={8}>
        <OptionCard opt={options.cost}
          onPick={() => onPick('cost')}
          picking={picking === 'cost'} />
      </Col>
      <Col xs={24} md={8}>
        <OptionCard opt={options.balanced}
          onPick={() => onPick('balanced')}
          picking={picking === 'balanced'} />
      </Col>
    </Row>
  )
}
